/**
 * Submits rows one at a time via `submitOne`, waiting for each submission to
 * settle before starting the next. Failures are recorded (not thrown) so the
 * remaining rows still get submitted.
 *
 * Pure orchestration — no Axios import here, so it is testable without
 * mocking HTTP. `submitOne` is injected by the caller.
 *
 * @param {Array} rows
 * @param {(row: any) => Promise<any>} submitOne
 * @param {(completed: number, total: number) => void} onProgress
 * @returns {Promise<Array>} one result per row, in row order
 */
export async function submitTradesSequentially(rows, submitOne, onProgress) {
  const total = rows.length;
  const results = [];

  for (const row of rows) {
    try {
      const result = await submitOne(row);
      results.push(result);
    } catch (error) {
      results.push({ row, status: "FAILED", error: error?.message });
    }
    onProgress(results.length, total);
  }

  return results;
}

/**
 * Formats `hedge_trade_error`/`errors` values for display. The backend's
 * `hedge_trade_error` is a Django JSONField — it is Eze's error list
 * (`[{tradeId, externalId, errorType, message}, ...]`), a single dict
 * (`{"message": "..."}` on a request failure), or absent — never a plain
 * string, except for legacy rows the old (pre-fix) code may have written.
 * Never pass the raw value directly into something that renders it as a
 * React child (e.g. MUI Tooltip's `title`) — objects/arrays there crash
 * React.
 *
 * @param {null|undefined|string|object|Array} err
 * @returns {string} empty string when there is nothing to show
 */
export function formatHedgeTradeError(err) {
  if (!err) return "";
  if (typeof err === "string") return err;
  const list = Array.isArray(err) ? err : [err];
  return list.map((e) => e?.message ?? JSON.stringify(e)).join("; ");
}

/**
 * Pairs each submitted row with its raw result (same order, as returned by
 * `submitTradesSequentially`) into a flat, renderable summary: symbol,
 * status, and any error/warning message. Pure — no React, no Axios.
 *
 * Handles both result shapes that can land in `results`:
 * - success: the raw endpoint response `{ status: "ok", result: [rowResult] }`
 *   (`rowResult` has `status`/`errors` — see `enter_convert_trades`).
 * - failure: `submitTradesSequentially`'s own `{ row, status: "FAILED", error }`,
 *   recorded when `submitOne` rejects before any response came back.
 *
 * @param {Array} rows - the rows submitted, in the same order passed to submitTradesSequentially
 * @param {Array} results - the results returned by submitTradesSequentially
 * @returns {Array<{symbol: string, status: string, message: string|null}>}
 */
export function buildSubmitResultRows(rows, results) {
  return results.map((raw, index) => {
    const row = rows[index] ?? {};
    const rowResult = raw?.result?.[0] ?? raw ?? {};
    const message =
      raw?.error ?? (rowResult?.errors ? formatHedgeTradeError(rowResult.errors) : null);

    return {
      symbol: row.hedge_symbol || row.security_symbol || "",
      status: rowResult?.status || "FAILED",
      message,
    };
  });
}
